import { matchPath } from 'react-router-dom';
import type { RouteConfig, RouteMeta } from '@/types';

export type { RouteMeta };

export const DEFAULT_TITLE = 'Dash Fleet Management';

const isParam = (segment: string): boolean => segment.startsWith(':');

/**
 * Derives the parent list route for a pattern by dropping trailing `/:param`
 * segments and then walking up one segment at a time until a route exists.
 * `/vehicles/:id` → `/vehicles`, `/settings/rent-escalation` → `/settings`,
 * `/2w/stnk` → undefined (no `/2w` route, so it is top-level).
 */
const deriveParentPath = (
  pattern: string,
  known: Set<string>
): string | undefined => {
  const segments = pattern.split('/').filter(Boolean);
  while (segments.length > 0) {
    // A candidate ending in a param is not navigable; keep walking up.
    if (isParam(segments[segments.length - 1])) {
      segments.pop();
      continue;
    }
    const candidate = `/${segments.join('/')}`;
    if (candidate !== pattern && known.has(candidate)) return candidate;
    segments.pop();
  }
  return undefined;
};

const countParams = (pattern: string): number =>
  pattern.split('/').filter(isParam).length;

const resolveRoute = (
  routeTable: RouteConfig[],
  pathname: string
): RouteConfig | undefined => {
  let best: RouteConfig | undefined;
  let bestParams = Number.POSITIVE_INFINITY;
  for (const route of routeTable) {
    if (route.path === '*') continue;
    if (!matchPath({ path: route.path, end: true }, pathname)) continue;
    const params = countParams(route.path);
    if (params < bestParams) {
      best = route;
      bestParams = params;
    }
  }
  return best;
};

/**
 * Per-table lookup cache. Resolving a pathname runs `matchPath` (which
 * compiles a RegExp) over the whole table, and this runs on every navigation
 * and every `ProtectedRoute` re-render — so the derived `Set` and the resolved
 * results are kept instead of rebuilt. Callers build the combined table inline
 * (`[DASHBOARD_ROUTE, HOME_ROUTE, ...routes]`), a fresh array each call, so the
 * cache is keyed by element-wise identity: ~40 reference comparisons, and the
 * route entries themselves are module constants.
 */
interface TableCache {
  table: RouteConfig[];
  known: Set<string>;
  routes: Map<string, RouteConfig | undefined>;
  metas: Map<string, RouteMeta>;
}

/** Pathnames carry ids, so the memo is bounded rather than unbounded. */
const MAX_CACHED_PATHS = 200;

let cache: TableCache | null = null;

const sameTable = (a: RouteConfig[], b: RouteConfig[]): boolean =>
  a.length === b.length && a.every((route, i) => route === b[i]);

const cacheFor = (routeTable: RouteConfig[]): TableCache => {
  if (cache && sameTable(cache.table, routeTable)) return cache;
  cache = {
    table: routeTable,
    known: new Set(routeTable.map((r) => r.path)),
    routes: new Map(),
    metas: new Map(),
  };
  return cache;
};

const remember = <T>(store: Map<string, T>, key: string, value: T): T => {
  if (store.size >= MAX_CACHED_PATHS) store.clear();
  store.set(key, value);
  return value;
};

/** Picks the most specific route whose pattern matches `pathname`. */
export const findRoute = (
  routeTable: RouteConfig[],
  pathname: string
): RouteConfig | undefined => {
  const entry = cacheFor(routeTable);
  if (entry.routes.has(pathname)) return entry.routes.get(pathname);
  return remember(entry.routes, pathname, resolveRoute(routeTable, pathname));
};

export const buildRouteMeta = (
  routeTable: RouteConfig[],
  pathname: string
): RouteMeta => {
  const entry = cacheFor(routeTable);
  const memoised = entry.metas.get(pathname);
  if (memoised) return memoised;

  const route = findRoute(routeTable, pathname);
  if (!route) {
    return remember(entry.metas, pathname, { mobileTitle: DEFAULT_TITLE });
  }

  const parentPath =
    route.parentPath ?? deriveParentPath(route.path, entry.known);

  return remember(entry.metas, pathname, {
    mobileTitle: route.mobileTitle ?? route.title,
    parentPath,
  });
};

/**
 * Document title for a pathname. Shares `findRoute` with `buildRouteMeta`, so
 * a detail route (`/vehicles/123` → `/vehicles/:id`) resolves to its own title
 * instead of falling back, and a static pattern still beats a param one
 * (`/packages/create` over `/packages/:id`).
 */
export const getRouteTitle = (
  routeTable: RouteConfig[],
  pathname: string
): string => findRoute(routeTable, pathname)?.title || DEFAULT_TITLE;
